import axiosInstanceCdn from "../Common/Axios/axiosInstanceCdn";

// Generic ("common") personalization rules (everyone, login+non-login alike),
// now served from S3, scoped per client: {clientCode}/personalization/common/common.json.
// The local /public/personalization/generic.json fixture is left in place for
// reference/manual testing but is no longer read at runtime.

const buildGenericConfigUrl = (clientCode) => `${process.env.REACT_APP_CLIENT_IMAGE_URL}${clientCode}/personalization/common/common.json`;
// const buildGenericConfigUrl = (clientCode) => "/personalization/generic.json";

// Member-specific rules — one file per member, named by their identifier,
// served from S3 same as the common config: {clientCode}/personalization/personalization_{encryptedMemberIdentifier}.json.
// Keyed off encryptedMemberIdentifier (not the plain memberIdentifier) so the
// S3 key/filename never carries a member's raw business identifier — same
// value the admin panel expects in the uploaded file's name (see
// Frontend/src/Utility/personalizationFileUrls.js, which mirrors this).
// Only ever fetched post-login, once both clientCode and encryptedMemberIdentifier
// are known. A member with no file at all just gets a 404 → fetchJson resolves
// to null → no member rules, generic still applies (AC9).
//
// encodeURIComponent is required, not cosmetic: a standard-Base64 encryptedMemberIdentifier
// can contain '+', '/', '=', and an unescaped '/' in particular would be read as an extra
// path segment rather than part of the filename. The backend escapes the identifier the
// same way (Uri.EscapeDataString) when it writes the S3 key on upload — see
// AssetProvider.BuildPersonalizationKey — so this must match it byte-for-byte or the file
// this builds a URL for will never resolve.
const buildMemberConfigUrl = (clientCode, encryptedMemberIdentifier) =>
  `${process.env.REACT_APP_CLIENT_IMAGE_URL}${clientCode}/personalization/personalization_${encodeURIComponent(encryptedMemberIdentifier)}.json`;
// const buildMemberConfigUrl = (clientCode, encryptedMemberIdentifier) => `/personalization/personalization_${encryptedMemberIdentifier}.json`;

// Goes through axiosInstanceCdn (plain axios, 5s timeout built in) instead of
// raw fetch, matching how every other API call in this codebase is made.
// axiosInstanceClient/axiosIntegration are NOT reused here on purpose — they
// unconditionally AES-decrypt response bodies for our own backend's encrypted
// responses, which would corrupt a plain public JSON file from the CDN.
// 404 means "no file for this client/member" — a normal, expected outcome,
// so it resolves to null rather than throwing. Anything else (timeout, CORS,
// 5xx) is re-thrown deliberately: both callers of this function run inside a
// try/catch in personalizationSlice.js that turns it into { rules: [] } and
// logs it via logPersonalizationError — the thunk itself never rejects, so
// this can never surface as a broken user journey on the Home Page.
const fetchJson = async (url) => {
  try {
    const { data } = await axiosInstanceCdn.get(url);
    return data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

export const callGenericPersonalizationConfigApi = async (clientCode) => {
  if (!clientCode) return null;
  return fetchJson(buildGenericConfigUrl(clientCode));
};
export const callPersonalizationConfigApi = async (clientCode, encryptedMemberIdentifier) => {
  if (!clientCode || !encryptedMemberIdentifier) return null;
  return fetchJson(buildMemberConfigUrl(clientCode, encryptedMemberIdentifier));
};
